import React from 'react';
import { FiShield, FiUsers, FiPhone } from 'react-icons/fi';
import {
  FormSection,
  SectionTitle,
  SubSectionTitle,
  FormGrid,
  FormGroup,
  Label,
  Input,
  Select,
  ToggleSwitch,
  ToggleInput,
  ToggleLabel,
  InfoBox
} from '../common/FormStyles';
import { GuaranteesEmployee } from '@/types/application-forms';
import { DocumentUpload, UploadedDocument } from '../common/DocumentUpload';
import { GUARANTEES_DOCUMENTS } from '@/config/documentsBySection';
import { Candidate } from '@/models';

interface GuaranteesSectionProps {
  data: Partial<Candidate>;
  onChange: (data: Partial<Candidate>) => void;
  uploadedDocuments?: UploadedDocument[];
  onDocumentUpload?: (type: string, file: File | null) => void;
  onDocumentRemove?: (type: string) => void;
  readonly?: boolean;
}

export const GuaranteesSection: React.FC<GuaranteesSectionProps> = ({
  data,
  onChange,
  uploadedDocuments = [],
  onDocumentUpload,
  onDocumentRemove,
  readonly = false
}) => {
  const handleChange = (field: string, value: any) => {
    onChange({ ...data, [field]: value });
  };

  const handleGarantChange = (field: string, value: any) => {
    const newGarant = { ...(data.garant || {}), [field]: value };
    handleChange('garant', newGarant);
  };

  return (
    <FormSection>
      <SectionTitle>
        <FiShield />
        Garanties et Références
      </SectionTitle>

      <InfoBox $type="info">
        <p>
          Les garanties et références renforcent votre dossier. Si vous avez un garant,
          ses informations seront vérifiées pour confirmer sa capacité à se porter garant.
        </p>
      </InfoBox>

      <SubSectionTitle>
        <FiUsers style={{ fontSize: '18px' }} />
        Garant / Aval
      </SubSectionTitle>

      <FormGroup>
        <Label>Avez-vous un garant ?</Label>
        <ToggleSwitch>
          <ToggleInput
            checked={data.a_garant || false}
            onChange={e => handleChange('a_garant', e.target.checked)}
            disabled={readonly}
          />
          <ToggleLabel>
            {data.a_garant ? 'Oui, j\'ai un garant' : 'Non, pas de garant'}
          </ToggleLabel>
        </ToggleSwitch>
      </FormGroup>

      {data.a_garant && (
        <>
          <FormGrid>
            <FormGroup>
              <Label $required>Nom du garant</Label>
              <Input
                type="text"
                value={data.garant?.nom || ''}
                onChange={e => handleGarantChange('nom', e.target.value)}
                placeholder="Nom"
                disabled={readonly}
              />
            </FormGroup>

            <FormGroup>
              <Label $required>Prénom du garant</Label>
              <Input
                type="text"
                value={data.garant?.prenoms || ''}
                onChange={e => handleGarantChange('prenoms', e.target.value)}
                placeholder="Prénom"
                disabled={readonly}
              />
            </FormGroup>
          </FormGrid>

          <FormGrid>
            <FormGroup>
              <Label $required>Lien avec le garant</Label>
              <Select
                value={data.garant?.lien || ''}
                onChange={e => handleGarantChange('lien', e.target.value)}
                disabled={readonly}
              >
                <option value="">Sélectionnez...</option>
                <option value="Parent">Parent</option>
                <option value="Ami">Ami</option>
                <option value="Collègue">Collègue</option>
                <option value="Chef coutumier">Chef coutumier</option>
                <option value="Autre">Autre</option>
              </Select>
            </FormGroup>

            {data.garant?.lien === 'Autre' && (
              <FormGroup>
                <Label>Précisez le lien</Label>
                <Input
                  type="text"
                  value={data.garant?.lien_autre || ''}
                  onChange={e => handleGarantChange('lien_autre', e.target.value)}
                  placeholder="Précisez..."
                  disabled={readonly}
                />
              </FormGroup>
            )}
          </FormGrid>

          <FormGrid>
            <FormGroup>
              <Label $required>Profession du garant</Label>
              <Input
                type="text"
                value={data.garant?.profession || ''}
                onChange={e => handleGarantChange('profession', e.target.value)}
                placeholder="Ex: Fonctionnaire, Commerçant..."
                disabled={readonly}
              />
            </FormGroup>

            <FormGroup>
              <Label>Revenus mensuels du garant (FCFA)</Label>
              <Input
                type="number"
                min="0"
                value={data.garant?.revenus_mensuels || ''}
                onChange={e => handleGarantChange('revenus_mensuels', parseInt(e.target.value) || 0)}
                placeholder="Ex: 500000"
                disabled={readonly}
              />
            </FormGroup>
          </FormGrid>

          <FormGroup>
            <Label $required>Téléphone du garant</Label>
            <Input
              type="tel"
              value={data.garant?.telephone || ''}
              onChange={e => handleGarantChange('telephone', e.target.value)}
              placeholder="+221 X XX XX XX XX"
              disabled={readonly}
            />
          </FormGroup>
        </>
      )}

      <SubSectionTitle>
        <FiPhone style={{ fontSize: '18px' }} />
        Références
      </SubSectionTitle>

      <InfoBox $type="warning">
        <p>
          Fournissez au moins 3 références qui pourront confirmer vos informations.
          Ces références seront contactées dans le cadre de l'instruction de votre dossier.
        </p>
      </InfoBox>

      <FormGrid>
        <FormGroup>
          <Label $required>Employeur actuel</Label>
          <Input
            type="text"
            value={data.employeur || ''}
            onChange={e => handleChange('employeur', e.target.value)}
            placeholder="Nom de l'entreprise"
            disabled={readonly}
          />
        </FormGroup>

        <FormGroup>
          <Label $required>Téléphone employeur</Label>
          <Input
            type="tel"
            value={data.telephone_employeur || ''}
            onChange={e => handleChange('telephone_employeur', e.target.value)}
            placeholder="+221 X XX XX XX XX"
            disabled={readonly}
          />
        </FormGroup>
      </FormGrid>

      <FormGrid>
        <FormGroup>
          <Label>Chef de quartier</Label>
          <Input
            type="text"
            value={data.chef_quartier_nom || ''}
            onChange={e => handleChange('chef_quartier_nom', e.target.value)}
            placeholder="Nom complet"
            disabled={readonly}
          />
        </FormGroup>

        <FormGroup>
          <Label>Téléphone chef de quartier</Label>
          <Input
            type="tel"
            value={data.chef_quartier_telephone || ''}
            onChange={e => handleChange('chef_quartier_telephone', e.target.value)}
            placeholder="+221 X XX XX XX XX"
            disabled={readonly}
          />
        </FormGroup>
      </FormGrid>

      <FormGrid>
        <FormGroup>
          <Label>Leader religieux</Label>
          <Input
            type="text"
            value={data.leader_religieux || ''}
            onChange={e => handleChange('leader_religieux', e.target.value)}
            placeholder="Nom complet"
            disabled={readonly}
          />
        </FormGroup>

        <FormGroup>
          <Label>Téléphone leader religieux</Label>
          <Input
            type="tel"
            value={data.leader_religieux_telephone || ''}
            onChange={e => handleChange('leader_religieux_telephone', e.target.value)}
            placeholder="+221 X XX XX XX XX"
            disabled={readonly}
          />
        </FormGroup>
      </FormGrid>

      {/* Documents justificatifs pour cette section */}
      {onDocumentUpload && onDocumentRemove && (
        <DocumentUpload
          sectionTitle="Garanties"
          documents={GUARANTEES_DOCUMENTS}
          uploadedFiles={uploadedDocuments}
          onFileSelect={onDocumentUpload}
          onFileRemove={onDocumentRemove}
          readonly={readonly}
        />
      )}
    </FormSection>
  );
};
